import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Eraser,
  Eye,
  EyeClosed,
  EyeOff,
  Search,
  Square,
  SquareCheck,
  Trash,
  X,
} from "lucide-react";
import { getData } from "~/functions/gridFunctions";
import Input from "../../RegisterForm/Input";
import { Scope } from "@unform/core";
import InputLine from "~/components/RegisterForm/InputLine";

const FindGeneric = ({
  defaultValue = null,
  InputContext = null,
  type = null,
  fields = [],
  route = "",
  title = "",
  scope = "",
  searchDefault = null,
  onlyDefaultvalue = false,
  required = false,
  readOnly = false,
  handleRemove = null,
  clearable = false,
  clearValue = null,
  allowedSearch = true,
  extraParams = null,
  onAttemptWithoutDep = null,
  setReturnFindGeneric = () => null,
  loadOptions = null,
  maxLimit = Infinity,
  ...rest
}) => {
  const searchRef = useRef();
  const optionsRequest = useRef(null);
  const [active, setActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(defaultValue || {});
  const [limit, setLimit] = useState(10);
  async function loadData(search = null) {
    if (loadOptions) {
      optionsRequest.current?.abort();
      const controller = new AbortController();
      optionsRequest.current = controller;
      const data = await loadOptions({ search: search || "", limit, signal: controller.signal });
      if (!controller.signal.aborted) setRows(data.filter(Boolean));
      return;
    }
    if (onlyDefaultvalue) {
      setRows([defaultValue]);
      return;
    }
    const data = await getData(route, {
      limit,
      search: {
        value: searchDefault ? searchDefault : search,
      },
      type,
      extraParams,
    });
    if (!data) {
      setRows([]);
      return;
    }
    setRows(data.filter(Boolean));
  }

  useEffect(() => {
    loadData();
  }, [limit, JSON.stringify(extraParams)]);

  useEffect(() => () => optionsRequest.current?.abort(), []);

  function handleActive() {
    setActive(!active);
    if (!active) {
      loadData();
    }
  }

  function handleSelect(row) {
    setSelected(row);
    handleActive();
    setSuccessfullyUpdated(false);
    setReturnFindGeneric(row);
  }
  function handleSelectWithoudReopen(row) {
    setSelected(row);
    setSuccessfullyUpdated(false);
    setReturnFindGeneric(row);
  }
  const { setSuccessfullyUpdated } = useContext(InputContext);

  return (
    <InputLine title={title}>
      <div className="flex flex-col justify-center items-start relative w-full">
        <div className="flex flex-row items-center justify-start gap-2 relative w-full">
          {!readOnly && handleRemove && (
            <button
              type="button"
              onClick={() => handleRemove(selected.id)}
              className="bg-white border rounded-sm p-2 mt-4 hover:bg-red-600 hover:text-white"
            >
              <Trash size={16} />
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={handleActive}
              aria-label={"Find " + title}
              className="bg-white border rounded-sm p-2 mt-4 hover:bg-gray-100"
            >
              {!active ? <Search size={16} /> : <X size={16} />}
            </button>
          )}
          {readOnly && onAttemptWithoutDep && (
            <button
              type="button"
              onClick={onAttemptWithoutDep}
              className="bg-white border rounded-sm p-2 mt-4 hover:bg-gray-100 opacity-50"
            >
              <Search size={16} />
            </button>
          )}
          <Scope path={scope}>
            <Input
              type="hidden"
              name="id"
              required={required}
              defaultValue={selected.id}
              InputContext={InputContext}
            />
            {fields.map((field, index) => {
              let retValue = selected[field.name];
              if (field.model && selected[field.model]) {
                retValue = selected[field.model];
                if (typeof selected[field.model] === "object") {
                  retValue = selected[field.model][field.name];
                }
              } else if (field.field && selected[field.field]) {
                retValue = selected[field.field].id;
              }
              if (
                retValue === undefined ||
                retValue === null ||
                retValue === ""
              ) {
                retValue = " ";
              }
              return (
                <Input
                  type={field.type || "text"}
                  name={field.name}
                  title={field.title}
                  required={index === 0 ? required : false}
                  readOnlyOnFocus
                  readOnly={index === 0 ? readOnly : true}
                  grow
                  defaultValue={retValue}
                  InputContext={InputContext}
                />
              );
            })}
          </Scope>
          {clearable && !(loadOptions && readOnly) && selected.id && (
            <button
              type="button"
              aria-label={"Clear " + title}
              onClick={() =>
                handleSelectWithoudReopen(
                  clearValue !== null ? clearValue : defaultValue
                )
              }
              className="bg-white border rounded-sm p-2 mt-4 hover:bg-red-600 hover:text-white"
            >
              <Eraser size={16} />
            </button>
          )}
        </div>
        {active && (
          <div className="flex flex-col items-center justify-start gap-2 relative w-full">
            {allowedSearch && (
              <div className="flex flex-row items-center justify-start gap-2 relative w-full mt-2 border-t pt-2">
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Type to search..."
                  className="bg-white border rounded-sm p-2 flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (loadOptions) e.preventDefault();
                      loadData(searchRef.current.value);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => loadData(searchRef.current.value)}
                  className="flex flex-row justify-between items-center gap-2 bg-primary text-white rounded-sm p-2 opacity-80 hover:opacity-100"
                >
                  <Search size={16} /> Search
                </button>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr>
                  <th className="bg-transparent border-0 rounded-sm p-2 hover:bg-gray-100 w-[43px]"></th>
                  {fields
                    .filter((field) => !field.field)
                    .map((field) => {
                      return (
                        <th className="bg-white border rounded-sm p-2 hover:bg-gray-100 text-left">
                          {field.title}
                        </th>
                      );
                    })}
                </tr>
              </thead>
              {rows.map((row, index) => {
                return (
                  <tr key={index}>
                    <td>
                      <button
                        type="button"
                        onClick={() => !(loadOptions && readOnly) && handleSelect(row)}
                        className="bg-transparent border-0 w-full text-center flex flex-row justify-center items-center"
                      >
                        {selected.id === row.id ? (
                          <SquareCheck size={16} />
                        ) : (
                          <Square size={16} />
                        )}
                      </button>
                    </td>
                    {fields
                      .filter((field) => !field.field)
                      .map((field) => {
                        return (
                          <td
                            onClick={() => !(loadOptions && readOnly) && handleSelect(row)}
                            className={`cursor-pointer bg-white border rounded-sm p-2 hover:bg-gray-100 text-left`}
                          >
                            {field.model2
                              ? row[field.model2]?.[field.model]?.[field.name]
                              : field.model
                              ? row[field.model]?.[field.name]
                              : row[field.name]}
                          </td>
                        );
                      })}
                  </tr>
                );
              })}
            </table>
            {rows.length >= 10 && (
              <div className="flex flex-row items-center justify-start gap-2 relative w-full">
                <button
                  type="button"
                  disabled={rows.length < limit || limit >= maxLimit}
                  onClick={() => setLimit(Math.min(limit + 10, maxLimit))}
                  className={`border rounded p-2 bg-secondary ${
                    rows.length < limit || limit >= maxLimit ? "text-gray-400" : "text-gray-800"
                  } text-xs flex flex-row items-center gap-2`}
                >
                  <Eye size={14} /> Show more
                </button>

                <button
                  type="button"
                  onClick={() => setLimit(10)}
                  disabled={limit === 10}
                  className={`border rounded p-2 bg-secondary ${
                    limit === 10 ? "text-gray-400" : "text-gray-800"
                  } text-xs flex flex-row items-center gap-2`}
                >
                  <EyeOff size={14} /> Minimize
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </InputLine>
  );
};

export default FindGeneric;
